/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { isRightToLeft } from 'js/lib/language';

import { IconButton } from '@coursera/cds-core';

import { LOHP_SLIDER_CARD_WIDTH } from 'bundles/front-page/common/constants';

import _t from 'i18n!nls/front-page';

type Props = {
  forcedCardWidth?: number;
  children: React.ReactNode[];
};

const styles = {
  container: css`
    margin: var(--cds-spacing-100) 1vw;
  `,
  horizontalScrollContainer: css`
    white-space: nowrap;
    position: relative;
    overflow-x: scroll;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    display: flex;
    scroll-snap-type: x mandatory;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  `,
  card: (forcedCardWidth?: number) => css`
    width: ${forcedCardWidth ? `${forcedCardWidth}vw` : `${LOHP_SLIDER_CARD_WIDTH}vw`};
    margin: 0 1vw;
    height: 100%;
  `,
  cardContainer: css`
    scroll-snap-align: start;
  `,
  controls: css`
    display: flex;
    justify-content: space-between;
    width: ${LOHP_SLIDER_CARD_WIDTH}vw;
    margin: var(--cds-spacing-100) 1vw 0;
  `,
  dots: css`
    height: 36px;
    border-radius: var(--cds-border-radius-max);
    background: var(--cds-color-neutral-background-primary-weak);
    display: flex;
    align-items: center;
    justify-content: space-evenly;
    padding: var(--cds-spacing-100);
  `,
  arrows: css`
    display: flex;
    gap: var(--cds-spacing-100);
  `,
  arrowButton: css`
    border-radius: 100%;
  `,
  dotsContainerWidth: (numDots: number) => css`
    width: ${numDots * 24}px;
  `,
  dot: css`
    width: 12px;
    height: 12px;
    border-radius: 50%;
  `,
  activeDot: css`
    background-color: var(--cds-color-neutral-primary-weak);
  `,
  inactiveDot: css`
    background-color: var(--cds-color-neutral-disabled-strong);
  `,
};

/**
 * A slider component that allows horizontal scrolling through a list of cards.
 * @param forcedCardWidth The width of each card in vw. Defaults to LOHP_SLIDER_CARD_WIDTH.
 * @param children The cards to display in the slider.
 * @returns A slider component.
 */
const Slider = ({ forcedCardWidth, children }: Props) => {
  const sliderRef = useRef<HTMLDivElement | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const isRTL = isRightToLeft(_t.getLocale());

  const calculateNewIndex = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (element: HTMLElement) => {
      // Converts vw to px, since leftScroll is in px
      const viewportWidth = forcedCardWidth
        ? (forcedCardWidth * Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0)) / 100
        : element.clientWidth;
      const leftScroll = isRTL ? -element.scrollLeft : element.scrollLeft;
      return Math.round(leftScroll / viewportWidth);
    },
    [forcedCardWidth, isRTL]
  );

  const handleScroll = useCallback<() => $TSFixMe>(() => {
    if (isAnimating) return;

    if (sliderRef.current) {
      const newActiveIndex = calculateNewIndex(sliderRef.current);
      if (newActiveIndex !== activeIndex) {
        setActiveIndex(newActiveIndex);
      }
    }
  }, [isAnimating, activeIndex, calculateNewIndex]);

  const scrollToIndex = (index: number) => {
    if (sliderRef.current) {
      const slider = sliderRef.current;
      const firstSlide = slider.firstChild as HTMLElement;
      const computedMarginLeft = getComputedStyle(firstSlide).marginLeft;
      const marginLeftValue = parseInt(computedMarginLeft, 10);
      const cardWidthWithMargin = firstSlide.offsetWidth + marginLeftValue * 2;

      setIsAnimating(true);

      slider.scrollTo({
        left: isRTL ? -(cardWidthWithMargin * index) : cardWidthWithMargin * index,
        behavior: 'smooth',
      });

      // Let animation finish before updating active index (avoids flickering dots)
      setTimeout(() => {
        setActiveIndex(index);
        setIsAnimating(false);
      }, 300);
    }
  };

  const handleNext = () => {
    if (!isAnimating) scrollToIndex(activeIndex + 1);
  };

  const handlePrevious = () => {
    if (!isAnimating) scrollToIndex(activeIndex - 1);
  };

  useEffect(() => {
    const currentSlide = sliderRef.current;

    if (currentSlide) {
      currentSlide.addEventListener('scroll', handleScroll);
    }

    return () => {
      currentSlide?.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  return (
    <div css={styles.container}>
      <div css={styles.horizontalScrollContainer} ref={sliderRef}>
        {children.map((child, index) => (
          <div css={styles.cardContainer} key={`slider-card-${index}`}>
            <div css={styles.card(forcedCardWidth)}>{child}</div>
          </div>
        ))}
      </div>
      <div css={styles.controls}>
        <div css={[styles.dots, styles.dotsContainerWidth(children.length)]}>
          {children.map((_, index) => (
            <div
              css={[styles.dot, index === activeIndex ? styles.activeDot : styles.inactiveDot]}
              key={`slider-display-dot-${index}`}
              data-testid={index === activeIndex ? `slider-display-dot-${index}-active` : `slider-display-dot-${index}`}
            />
          ))}
        </div>
        <div css={styles.arrows}>
          <IconButton
            css={styles.arrowButton}
            onClick={handlePrevious}
            variant="secondary"
            intent="previous"
            size="small"
            disabled={activeIndex === 0}
          />
          <IconButton
            css={styles.arrowButton}
            onClick={handleNext}
            variant="secondary"
            intent="next"
            size="small"
            disabled={activeIndex === children.length - 1}
          />
        </div>
      </div>
    </div>
  );
};

export default Slider;
